import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DemoBadge, EmptyState, InfoNote, PriorityBadge } from "@/components/shared";
import { MapPin, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Locations() {
  const { activeWebsite } = useApp();
  const [strategy, setStrategy] = useState(null);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLoc, setNewLoc] = useState("");

  const load = async () => {
    if (!activeWebsite) { setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data: s }, { data: o }] = await Promise.all([
        api.get(`/websites/${activeWebsite.id}/strategy`),
        api.get(`/websites/${activeWebsite.id}/opportunities`, { params: { otype: "location", status: "all" } }),
      ]);
      setStrategy(s);
      setOpps(o.items);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeWebsite?.id]); // eslint-disable-line

  const saveLocations = async (locs) => {
    try {
      const { data } = await api.put(`/websites/${activeWebsite.id}/strategy`, { ...strategy, target_locations: locs });
      setStrategy(data);
      toast.success("Target locations updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addLoc = () => {
    const v = newLoc.trim();
    if (!v) return;
    const locs = [...(strategy?.target_locations || []), v];
    setNewLoc("");
    saveLocations(locs);
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Locations</h1>
        <p className="text-xs text-muted-foreground">Configure target locations; the engine surfaces location opportunities only where real search evidence exists.</p>
      </div>

      <Card className="shadow-none">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Target locations</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {(strategy?.target_locations || []).map((l) => (
              <Badge key={l} variant="outline" className="gap-1 pr-1 text-xs" data-testid="location-target-badge">
                {l}
                <button onClick={() => saveLocations(strategy.target_locations.filter((x) => x !== l))} className="rounded-full p-0.5 hover:bg-muted" data-testid="location-remove-button">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(strategy?.target_locations || []).length === 0 && <span className="text-xs text-muted-foreground">No target locations configured yet.</span>}
          </div>
          <div className="flex gap-2">
            <Input value={newLoc} onChange={(e) => setNewLoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLoc()}
              placeholder="e.g. Manchester, Brooklyn, Richmond…" className="h-8 w-64 text-xs" data-testid="location-add-input" />
            <Button size="sm" className="h-8" onClick={addLoc} data-testid="location-add-button"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
          </div>
          <InfoNote>
            Location pages must be genuinely useful. The platform never recommends fake offices, fake teams or templated
            city-swap pages - opportunities appear only when queries mentioning a location show real impressions.
          </InfoNote>
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-2 text-sm font-semibold">Location opportunities</h2>
        {opps.length === 0 ? (
          <EmptyState icon={MapPin} title="No location opportunities yet"
            message="Add target locations above and connect Search Console. Opportunities appear when location-related queries have meaningful impressions but no dedicated page." testId="locations-empty" />
        ) : (
          <div className="space-y-2">
            {opps.map((o) => (
              <Card key={o.id} className="shadow-none" data-testid="location-opportunity-card">
                <CardContent className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <PriorityBadge priority={o.priority} />
                    <span className="text-sm font-semibold">{o.evidence?.location}</span>
                    {o.is_demo && <DemoBadge />}
                    <span className="ml-auto text-sm font-semibold tabular-nums">{o.score}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{o.why}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(o.evidence?.example_queries || []).map((q) => <Badge key={q} variant="outline" className="font-mono text-[10px]">{q}</Badge>)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
